import { useRef, useCallback, useState } from 'react';
import L from 'leaflet';
import 'leaflet-snap';
import {
  AREA_TYPES, getAreaTypeColor, getAreaTypeLabel, DEFAULT_LINE_WIDTH,
} from '../../SiteTakeoff/siteTakeoffConstants';
import { enhancedGetPolygonArea, getGeometryFromLayer } from '../../MapWithDrawing/Measure/mapUtils';
import { buildPointMarkerArea, buildCircleArea } from '../utils/markerArea';

// Haversine distance for polyline length in feet
const calculateLineLengthFeet = (paths) => {
  if (!paths || paths.length < 2) return 0;
  let totalMeters = 0;
  for (let i = 1; i < paths.length; i++) {
    const p1 = L.latLng(paths[i - 1].lat, paths[i - 1].lng);
    const p2 = L.latLng(paths[i].lat, paths[i].lng);
    totalMeters += p1.distanceTo(p2);
  }
  return totalMeters * 3.28084;
};

/**
 * Exposes `startDraw('polygon' | 'polyline' | 'point' | 'ellipse')`.
 * Each call installs a ONE-SHOT draw:created listener — no persistent
 * subscription, so it doesn't conflict with jobsite-layer drawing.
 *
 * Snapping: when enabled, vertices snap to edges/vertices of existing
 * area layers within 20px. Toggle via `setSnapping(bool)`.
 *
 * Drawing preview: polygon fill is transparent while drawing so the
 * outline doesn't obscure existing shapes underneath.
 */
export default function useAreaDrawing({ mapRef, drawnItemsRef, onAreaDrawn }) {
  const handlerRef = useRef(null);
  const [snapping, setSnapping] = useState(true);

  // Collect guide layers from drawnItems for snap targets
  const getGuideLayers = useCallback(() => {
    const di = drawnItemsRef.current;
    if (!di) return [];
    const layers = [];
    di.eachLayer(l => { if (l instanceof L.Polyline) layers.push(l); });
    return layers;
  }, [drawnItemsRef]);

  const startDraw = useCallback((tool, overrides) => {
    const map = mapRef.current;
    if (!map) return;

    // Disable any existing handler first
    if (handlerRef.current?.disable) {
      try { handlerRef.current.disable(); } catch (_) {}
      handlerRef.current = null;
    }

    const snapOpts = snapping ? {
      guideLayers: getGuideLayers(),
      snapDistance: 20,
      snapVertices: true,
    } : {};

    let handler;
    switch (tool) {
      case 'polygon':
        handler = new L.Draw.Polygon(map, {
          shapeOptions: {
            color: '#1976d2',
            weight: 2,
            fillOpacity: 0,       // no fill while drawing — outline only
            opacity: 0.9,
          },
          allowIntersection: false,
          showArea: true,
          ...snapOpts,
        });
        break;
      case 'polyline':
      case 'line':
        handler = new L.Draw.Polyline(map, {
          shapeOptions: { color: '#1976d2', weight: 4 },
          ...snapOpts,
        });
        break;
      case 'point':
      case 'marker':
        handler = new L.Draw.Marker(map, {
          ...snapOpts,
        });
        break;
      case 'circle':
      case 'ellipse':
        handler = new L.Draw.Circle(map, {
          shapeOptions: { color: '#1976d2', weight: 2, fillOpacity: 0 },
        });
        break;
      default:
        return;
    }

    // One-shot listener — detaches itself after firing
    const listener = (e) => {
      map.off(L.Draw.Event.CREATED, listener);
      handlerRef.current = null;

      const layer = e.layer;
      const isPolyline = (layer instanceof L.Polyline) && !(layer instanceof L.Polygon);
      const isCircle = (layer instanceof L.Circle);
      const isMarker = (layer instanceof L.Marker);

      let newArea;

      if (isMarker) {
        const ll = layer.getLatLng();
        newArea = buildPointMarkerArea({ lat: ll.lat, lng: ll.lng });
      } else if (isCircle) {
        const center = layer.getLatLng();
        newArea = buildCircleArea({ lat: center.lat, lng: center.lng, radiusMeters: layer.getRadius() });
      } else if (isPolyline) {
        const latlngs = layer.getLatLngs();
        const paths = latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
        const lengthFeet = calculateLineLengthFeet(paths);
        const defaultType = AREA_TYPES.TR_DG;
        const widthFt = DEFAULT_LINE_WIDTH;
        const areaSF = lengthFeet * widthFt;
        newArea = {
          geometryType: 'polyline',
          name: getAreaTypeLabel(defaultType),
          areaType: defaultType,
          paths,
          length: lengthFeet.toFixed(2),
          width: widthFt,
          area: areaSF.toFixed(2),
          areaAcres: (areaSF / 43560).toFixed(4),
          perimeter: '0',
          fillColor: getAreaTypeColor(defaultType),
          strokeColor: '#000000',
        };
      } else {
        // polygon
        const geometry = getGeometryFromLayer(layer);
        const latlngs = geometry?.latlngs || [];
        const paths = latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
        const measurements = enhancedGetPolygonArea(paths);
        const defaultType = AREA_TYPES.NT_TURF_OPEN;
        newArea = {
          geometryType: 'polygon',
          name: getAreaTypeLabel(defaultType),
          areaType: defaultType,
          paths,
          area: (measurements.areaFeet || 0).toFixed(2),
          areaAcres: (measurements.areaAcres || 0).toFixed(4),
          perimeter: (measurements.perimeterFeet || 0).toFixed(2),
          fillColor: getAreaTypeColor(defaultType),
          strokeColor: '#000000',
        };
      }

      // Remove the temporary draw layer — useAreaRendering adds the permanent styled version
      try {
        if (drawnItemsRef.current) {
          drawnItemsRef.current.removeLayer(layer);
        }
      } catch (_) {}

      if (onAreaDrawn) onAreaDrawn({ ...newArea, ...overrides });
    };

    map.on(L.Draw.Event.CREATED, listener);
    handler.enable();
    handlerRef.current = handler;
  }, [mapRef, drawnItemsRef, onAreaDrawn, snapping, getGuideLayers]);

  const cancelDraw = useCallback(() => {
    if (handlerRef.current?.disable) {
      try { handlerRef.current.disable(); } catch (_) {}
      handlerRef.current = null;
    }
  }, []);

  return { startDraw, cancelDraw, snapping, setSnapping };
}
